import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClipboardList, faSlidersH, faChevronDown, faTachometerAlt, faStore, faStar, faCheckCircle, faImages, faChevronRight, faInbox, faShieldAlt,
} from '@fortawesome/free-solid-svg-icons';

import { useGarage } from './GarageContext';
import RecordSheet from './RecordSheet';
import Plate from './Plate';
import { formatKm, formatMoney, formatMonthYear, formatPlate, formatShortDate, monthKey, plural, recordTitle } from './format';

const PAGE_SIZE = 20;

const vehicleName = (vehicle) => [vehicle.brand, vehicle.model].filter(Boolean).join(' ') || 'Veículo';

function RecordCard({ record, showPlate, onOpen }) {
  return (
    <li className='gs-event'>
      <span className='gs-event-dot' aria-hidden='true' />
      <button type='button' className='gs-event-card' onClick={() => onOpen(record)}>
        <span className='gs-event-date'>{formatShortDate(record.date)}</span>
        <span className='gs-event-body'>
          <strong className='gs-event-title'>{recordTitle(record)}</strong>
          <span className='gs-event-company'>
            <FontAwesomeIcon icon={faStore} aria-hidden='true' />
            {record.company.name}
          </span>
          <span className='gs-event-meta'>
            {showPlate && <Plate value={record.plate} size='sm' />}
            {record.km ? <span><FontAwesomeIcon icon={faTachometerAlt} aria-hidden='true' />{formatKm(record.km)}</span> : null}
            {record.total !== null && <span className='gs-event-value'>{formatMoney(record.total)}</span>}
            {record.photos.length > 0 && <span><FontAwesomeIcon icon={faImages} aria-hidden='true' />{record.photos.length}</span>}
            {record.evaluated && <span className='gs-event-ok'><FontAwesomeIcon icon={faCheckCircle} aria-hidden='true' />Avaliado</span>}
          </span>
          {record.canEvaluate && <span className='gs-chip gs-chip-attention gs-event-chip'><FontAwesomeIcon icon={faStar} aria-hidden='true' />Avaliar atendimento</span>}
        </span>
        <FontAwesomeIcon icon={faChevronRight} className='gs-event-chevron' aria-hidden='true' />
      </button>
    </li>
  );
}

export default function HistoryPage() {
  const { loading, vehicles, error } = useGarage();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const [plate, setPlate] = useState(params.get('placa') || '');
  const [company, setCompany] = useState('');
  const [year, setYear] = useState('');
  const [onlyPending, setOnlyPending] = useState(params.get('avaliar') === '1');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [selected, setSelected] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Um atendimento aparece uma vez so, mesmo que a API o devolva para mais de uma placa.
  const allRecords = useMemo(() => {
    const unique = new Map();
    vehicles.forEach((v) => v.records.forEach((r) => { if (!unique.has(r.key)) unique.set(r.key, r); }));
    return [...unique.values()].sort((a, b) => (b.date ? b.date.getTime() : 0) - (a.date ? a.date.getTime() : 0));
  }, [vehicles]);

  const companies = useMemo(
    () => [...new Set(allRecords.map((r) => r.company.name))].sort((a, b) => a.localeCompare(b, 'pt-BR')),
    [allRecords],
  );
  const years = useMemo(
    () => [...new Set(allRecords.filter((r) => r.date).map((r) => r.date.getUTCFullYear()))].sort((a, b) => b - a),
    [allRecords],
  );

  const filtered = allRecords.filter((r) =>
    (!plate || r.plate === plate) &&
    (!company || r.company.name === company) &&
    (!year || (r.date && r.date.getUTCFullYear() === Number(year))) &&
    (!onlyPending || r.canEvaluate));

  const groups = [];
  filtered.slice(0, visible).forEach((record) => {
    const key = monthKey(record.date);
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.records.push(record);
    else groups.push({ key, label: formatMonthYear(record.date), records: [record] });
  });

  const selectedVehicle = vehicles.find((v) => v.licensePlate === plate);
  const hasFilters = plate || company || year || onlyPending;
  const extraFilters = [company, year, onlyPending].filter(Boolean).length;
  const clearFilters = () => { setPlate(''); setCompany(''); setYear(''); setOnlyPending(false); };

  return (
    <div className='gs-stack'>
      <header className='gs-page-header'>
        <span className='gs-eyebrow'>Histórico</span>
        <h1>A história dos seus veículos</h1>
        <p className='gs-trust'>
          <FontAwesomeIcon icon={faShieldAlt} aria-hidden='true' />
          Cada registro é enviado pela empresa responsável pelo atendimento.
        </p>
      </header>

      {error && <p className='gs-notice gs-notice-error' role='alert'>{error}</p>}

      {/* Sem nenhum registro na conta os filtros nao tem o que filtrar. */}
      {vehicles.length > 0 && allRecords.length > 0 && (
        <section className='gs-filters' aria-label='Filtrar histórico'>
          {vehicles.length > 1 && (
            // Seletor unico: escala para qualquer quantidade de veiculos e abre o seletor nativo do celular.
            <label className='gs-vehicle-picker'>
              <span className='gs-vehicle-picker-label'>Veículo</span>
              <span className='gs-vehicle-picker-value' aria-hidden='true'>
                {selectedVehicle
                  ? <><Plate value={selectedVehicle.licensePlate} size='sm' /><span>{vehicleName(selectedVehicle)}</span></>
                  : <span>Todos os veículos</span>}
                <FontAwesomeIcon icon={faChevronDown} className='gs-vehicle-picker-chevron' />
              </span>
              <select value={plate} onChange={(e) => setPlate(e.target.value)}>
                <option value=''>Todos os veículos ({allRecords.length})</option>
                {/* A mesma placa pode estar cadastrada duas vezes na conta: aparece uma vez so. */}
                {vehicles.filter((v, i, list) => list.findIndex((o) => o.licensePlate === v.licensePlate) === i).map((v) => (
                  <option key={v.licensePlate} value={v.licensePlate}>
                    {vehicleName(v)} · {formatPlate(v.licensePlate)} ({v.records.length})
                  </option>
                ))}
              </select>
            </label>
          )}
          <button
            type='button'
            className='gs-text-btn gs-filters-toggle'
            aria-expanded={filtersOpen}
            aria-controls='gs-more-filters'
            onClick={() => setFiltersOpen((open) => !open)}
          >
            <FontAwesomeIcon icon={faSlidersH} aria-hidden='true' />
            Filtros
            {extraFilters > 0 && <span className='gs-filters-count'>{extraFilters}</span>}
          </button>
          <div id='gs-more-filters' className={`gs-selects${filtersOpen ? ' is-open' : ''}`}>
            <label className='gs-select'>
              <span>Empresa</span>
              <select value={company} onChange={(e) => setCompany(e.target.value)}>
                <option value=''>Todas</option>
                {companies.map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
            </label>
            <label className='gs-select'>
              <span>Ano</span>
              <select value={year} onChange={(e) => setYear(e.target.value)}>
                <option value=''>Todos</option>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>
            <label className='gs-toggle'>
              <input type='checkbox' checked={onlyPending} onChange={(e) => setOnlyPending(e.target.checked)} />
              <span>Só para avaliar</span>
            </label>
          </div>
          {!loading && (
            <p className='gs-count' aria-live='polite'>
              {filtered.length} {plural(filtered.length, 'registro', 'registros')}
              {hasFilters && <button type='button' className='gs-link' onClick={clearFilters}>Limpar filtros</button>}
            </p>
          )}
        </section>
      )}

      {loading && <div className='gs-timeline-skeleton' aria-busy='true'><div className='gs-skeleton' /><div className='gs-skeleton' /></div>}

      {!loading && !error && vehicles.length === 0 && (
        <section className='gs-empty'>
          <span className='gs-icon gs-icon-orange gs-icon-lg' aria-hidden='true'><FontAwesomeIcon icon={faClipboardList} /></span>
          <h2>Adicione um veículo para ver o histórico</h2>
          <p>Os atendimentos aparecem aqui assim que a placa estiver na sua garagem.</p>
          <Link className='gs-btn gs-btn-primary' to='/client/newvehicle'>Adicionar à minha garagem</Link>
        </section>
      )}

      {!loading && vehicles.length > 0 && filtered.length === 0 && (
        <section className='gs-empty'>
          <span className='gs-empty-icon' aria-hidden='true'><FontAwesomeIcon icon={faInbox} /></span>
          <h2>{extraFilters > 0 ? 'Nenhum registro com esses filtros' : plate ? 'Ainda não encontramos registros para este veículo.' : 'Ainda não encontramos registros para os seus veículos.'}</h2>
          <p>
            {extraFilters > 0
              ? 'Tente outra empresa ou outro ano.'
              : 'Quando uma empresa parceira registrar um atendimento, ele aparecerá aqui.'}
          </p>
        </section>
      )}

      {!loading && groups.length > 0 && (
        <div className='gs-timeline'>
          {groups.map((group) => (
            <section key={group.key} className='gs-month' aria-label={group.label}>
              <h2 className='gs-month-title'>{group.label}</h2>
              <ol className='gs-events'>
                {group.records.map((record) => (
                  <RecordCard key={record.key} record={record} showPlate={!plate && vehicles.length > 1} onOpen={setSelected} />
                ))}
              </ol>
            </section>
          ))}
          {filtered.length > visible && (
            <button type='button' className='gs-btn gs-btn-secondary gs-more' onClick={() => setVisible((v) => v + PAGE_SIZE)}>
              Mostrar mais registros
            </button>
          )}
        </div>
      )}

      {selected && <RecordSheet record={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
